import type {
  PurchaseTrendForecastUnavailableReason,
  PurchaseTrendMonthValues,
  PurchaseTrendResponse,
  PurchaseTrendSupplierSeries,
  PurchaseTrendUnmatchedSupplier,
} from '@erp/rpc';
import type { FieldMappings } from '@erp/shared-types';
import { formatCurrency } from '@erp/shared-utils';
import type { MultiSelectOption } from '@/components/common/MultiSelect';
import { SUPPLIER_GROUP_MAP } from './supplierGroups';
import {
  buildPurchaseTrendChartRows,
  buildPurchaseTrendSubtitle,
  forecastMonthsWithAmount,
  PURCHASE_CURRENCY,
  supplierDataThroughLabel,
  type PurchaseTrendChartRow,
  type PurchaseTrendSubtitleModel,
} from './purchaseTrendChart';

export interface PurchaseYearOption {
  id: string;
  label: string;
  value: number;
}

export interface PurchaseSupplierOption {
  id: string;
  label: string;
  value: string;
  code?: string;
  companyId?: string;
}

export interface PurchaseUploadOption {
  id: string;
  label: string;
  value: string;
  fileName: string;
}

export interface LastUploadRecord {
  fileName: string;
  uploadedAt: Date;
  throughMonth: number;
  year: number;
}

export type LastUploadCardModel =
  | {
      kind: 'session';
      fileName: string;
      uploadedAt: Date;
      throughLabel: string;
    }
  | { kind: 'inferred'; throughLabel: string }
  | { kind: 'empty' };

export type PurchaseChartStatus = 'loading' | 'error' | 'ready';

export function yearsFromAgreementData(
  yearsData: { years?: number[] } | null | undefined,
  currentYear: number,
): number[] {
  if (yearsData && 'years' in yearsData && yearsData.years) {
    return yearsData.years;
  }
  return [currentYear];
}

export function buildYearOptions(
  years: number[],
  currentYear: number,
): PurchaseYearOption[] {
  const unique = new Set(years.filter((year) => year <= currentYear));
  unique.add(currentYear);
  return Array.from(unique)
    .sort((left, right) => right - left)
    .map((year) => ({
      id: String(year),
      label: String(year),
      value: year,
    }));
}

export function buildSupplierOptions(
  suppliers:
    | Array<{ id: string; name: string; code?: string; companyId?: string }>
    | undefined,
): PurchaseSupplierOption[] {
  return (suppliers ?? []).map((supplier) => ({
    id: supplier.id,
    label: supplier.name,
    value: supplier.id,
    ...(supplier.code !== undefined ? { code: supplier.code } : {}),
    ...(supplier.companyId !== undefined
      ? { companyId: supplier.companyId }
      : {}),
  }));
}

export function buildDivisionOptions(
  divisions: Array<{ id: string; name: string }> | undefined,
): MultiSelectOption[] {
  return (divisions ?? []).map((division) => ({
    id: division.id,
    label: division.name,
    value: division,
  }));
}

export function trendFilterIds(
  selected: MultiSelectOption[],
): string[] | undefined {
  if (selected.length === 0) {
    return undefined;
  }
  return selected.map((option) => option.id);
}

export type PurchaseTrendLockedFilters = {
  year: number;
  divisionIds?: string[];
  uploadId?: string;
  supplierIds?: string[];
};

export function lockedLabelsFromNamed(
  ids: string[] | undefined,
  named: Array<{ id: string; name?: string; label?: string }>,
): Record<string, string> {
  const allow = new Set(ids ?? []);
  const labels: Record<string, string> = {};
  for (const row of named) {
    if (!allow.has(row.id)) {
      continue;
    }
    const label = row.label ?? row.name;
    if (label) {
      labels[row.id] = label;
    }
  }
  return labels;
}

export function optionsFromLockedIds(
  ids: string[] | undefined,
  labelsById: Record<string, string> = {},
  unlabeled = 'Shared filter',
): MultiSelectOption[] {
  return (ids ?? []).map((id) => ({
    id,
    label: labelsById[id] ?? unlabeled,
    value: id,
  }));
}

export function purchaseTrendEffectiveYear(
  locked: PurchaseTrendLockedFilters | undefined,
  selectedYear: number,
): number {
  return locked?.year ?? selectedYear;
}

export function defaultPurchaseUploadYear(now: Date): number {
  return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
}

export function purchaseTrendPageYear(input: {
  lockedYear?: number;
  currentYear: number;
  currentUploads: Array<{ superseded: boolean; uploadedAt: Date }>;
  priorUploads: Array<{ superseded: boolean; uploadedAt: Date }>;
}): number {
  if (input.lockedYear !== undefined) {
    return input.lockedYear;
  }
  const currentLatest = input.currentUploads.find((row) => !row.superseded);
  const priorLatest = input.priorUploads.find((row) => !row.superseded);
  if (currentLatest && priorLatest) {
    return currentLatest.uploadedAt >= priorLatest.uploadedAt
      ? input.currentYear
      : input.currentYear - 1;
  }
  if (currentLatest) {
    return input.currentYear;
  }
  if (priorLatest) {
    return input.currentYear - 1;
  }
  return input.currentYear;
}

export function purchaseTrendEffectiveUploadId(
  locked: PurchaseTrendLockedFilters | undefined,
  selectedUploadId: string | null,
): string | null {
  return locked?.uploadId ?? selectedUploadId;
}

export function purchaseTrendShareMetadata(uploadId: string | null): {
  uploadId?: string;
} {
  return uploadId ? { uploadId } : {};
}

export function purchaseTrendDownloadModalCopy(
  format: 'csv' | 'xlsx',
  labels: {
    xlsxTitle: string;
    csvTitle: string;
    xlsxConfirm: string;
    csvConfirm: string;
  },
): { title: string; confirmLabel: string } {
  if (format === 'xlsx') {
    return { title: labels.xlsxTitle, confirmLabel: labels.xlsxConfirm };
  }
  return { title: labels.csvTitle, confirmLabel: labels.csvConfirm };
}

export function purchaseTrendSupplierInput(selected: MultiSelectOption[]): {
  supplierId?: string;
  supplierIds?: string[];
} {
  const ids = trendFilterIds(selected);
  if (ids === undefined) {
    return {};
  }
  if (ids.length === 1) {
    const supplierId = ids[0];
    return supplierId === undefined ? {} : { supplierId };
  }
  return { supplierIds: ids };
}

export type PurchaseTrendTitleFilter =
  { kind: 'names'; names: string[] } | { kind: 'count'; count: number };

export function purchaseTrendTitleFilters(
  labels: string[],
  maxNamed = 2,
): PurchaseTrendTitleFilter | null {
  if (labels.length === 0) {
    return null;
  }
  if (labels.length > maxNamed) {
    return { kind: 'count', count: labels.length };
  }
  return { kind: 'names', names: labels };
}

export interface PurchaseTrendTitleModel {
  year: number;
  suppliers: PurchaseTrendTitleFilter | null;
  divisions: PurchaseTrendTitleFilter | null;
}

export function buildPurchaseTrendTitle(input: {
  year: number;
  supplierLabels: string[];
  divisionLabels: string[];
}): PurchaseTrendTitleModel {
  return {
    year: input.year,
    suppliers: purchaseTrendTitleFilters(input.supplierLabels),
    divisions: purchaseTrendTitleFilters(input.divisionLabels),
  };
}

export function selectPurchaseTrendSeries(
  data: PurchaseTrendResponse | undefined,
  supplierId: string | null,
): PurchaseTrendSupplierSeries | null {
  if (!data || !supplierId) {
    return null;
  }
  return (
    data.suppliers.find((supplier) => supplier.supplierId === supplierId) ??
    null
  );
}

export function buildUploadOptions(
  uploads: Array<{
    id: string;
    fileName: string;
    uploadedAt: Date;
    throughMonth: number;
    supersededAt: Date | null;
    dataMissing?: boolean;
  }>,
  monthLabels: string[],
  labels: { latest: string; replacedOn: string; dataMissing: string },
): PurchaseUploadOption[] {
  const newestId = uploads[0]?.id;
  return uploads.map((upload) => {
    const throughLabel = monthLabels[upload.throughMonth - 1] ?? '';
    const uploadedDate = upload.uploadedAt.toLocaleDateString();
    const base = `${upload.fileName} · ${uploadedDate} · ${throughLabel}`;
    const suffix =
      upload.id === newestId
        ? ` · ${labels.latest}`
        : upload.supersededAt
          ? ` · ${labels.replacedOn} ${upload.supersededAt.toLocaleDateString()}`
          : '';
    const missing = upload.dataMissing ? ` · ${labels.dataMissing}` : '';
    return {
      id: upload.id,
      label: `${base}${suffix}${missing}`,
      value: upload.id,
      fileName: upload.fileName,
    };
  });
}

export function resolveSelectedUploadId(
  uploads: Array<{ id: string }>,
  selectedUploadId: string | null,
): string | null {
  if (
    selectedUploadId &&
    (uploads.length === 0 ||
      uploads.some((upload) => upload.id === selectedUploadId))
  ) {
    return selectedUploadId;
  }
  return uploads[0]?.id ?? null;
}

export function selectedUploadRecord(
  uploads: Array<{
    id: string;
    fileName: string;
    uploadedAt: Date;
    throughMonth: number;
  }>,
  selectedUploadId: string | null,
  year: number,
): LastUploadRecord | null {
  const upload = uploads.find((item) => item.id === selectedUploadId);
  if (!upload) {
    return null;
  }
  return {
    fileName: upload.fileName,
    uploadedAt: upload.uploadedAt,
    throughMonth: upload.throughMonth,
    year,
  };
}

export function uploadInViewRecord(
  uploadsInView:
    | Array<{
        id: string;
        fileName: string;
        uploadedAt: Date;
        throughMonth: number;
      }>
    | undefined,
  resolvedUploadId: string | null,
  year: number,
): LastUploadRecord | null {
  const uploadId = uploadsInView?.some(
    (upload) => upload.id === resolvedUploadId,
  )
    ? resolvedUploadId
    : (uploadsInView?.[0]?.id ?? null);
  return selectedUploadRecord(uploadsInView ?? [], uploadId, year);
}

export function lastUploadCardModel(input: {
  lastUpload: LastUploadRecord | null;
  selectedUpload?: LastUploadRecord | null;
  uploadInView?: LastUploadRecord | null;
  selectedYear: number;
  monthLabels: string[];
  inferredThroughMonth: number | null;
}): LastUploadCardModel {
  const upload =
    input.uploadInView ??
    input.selectedUpload ??
    (input.lastUpload && input.lastUpload.year === input.selectedYear
      ? input.lastUpload
      : null);
  if (upload) {
    return {
      kind: 'session',
      fileName: upload.fileName,
      uploadedAt: upload.uploadedAt,
      throughLabel: input.monthLabels[upload.throughMonth - 1] ?? '',
    };
  }
  if (input.inferredThroughMonth !== null) {
    return {
      kind: 'inferred',
      throughLabel: input.monthLabels[input.inferredThroughMonth - 1] ?? '',
    };
  }
  return { kind: 'empty' };
}

export function purchaseChartStatus(
  isLoading: boolean,
  error: unknown,
): PurchaseChartStatus {
  if (isLoading) {
    return 'loading';
  }
  if (error) {
    return 'error';
  }
  return 'ready';
}

export function purchaseUploadsStatus(input: {
  primary: { data?: unknown; isLoading: boolean };
  prior: { data?: unknown; isLoading: boolean };
  lockedYear?: number;
}): { uploadsLoading: boolean; uploadsLoaded: boolean } {
  const usesPrior = input.lockedYear === undefined;
  return {
    uploadsLoading:
      input.primary.isLoading || (usesPrior && input.prior.isLoading),
    uploadsLoaded:
      input.primary.data !== undefined &&
      (!usesPrior || input.prior.data !== undefined),
  };
}

export function purchaseTrendIsSettled(input: {
  hasNoFile: boolean;
  suppliersLoading: boolean;
  divisionsLoading: boolean;
  uploadsLoading: boolean;
  trendLoading: boolean;
  hasTrendData: boolean;
  hasTrendError: boolean;
}): boolean {
  if (input.hasNoFile) {
    return true;
  }
  return (
    !input.suppliersLoading &&
    !input.divisionsLoading &&
    !input.uploadsLoading &&
    !input.trendLoading &&
    (input.hasTrendData || input.hasTrendError)
  );
}

export function purchaseTrendInitialLoading(input: {
  hasCompletedInitialLoad: boolean;
  hasNoFile: boolean;
  suppliersLoading: boolean;
  divisionsLoading: boolean;
  uploadsLoading: boolean;
  trendLoading: boolean;
  hasTrendData: boolean;
  hasTrendError: boolean;
}): boolean {
  if (input.hasCompletedInitialLoad) {
    return false;
  }
  if (input.hasNoFile) {
    return false;
  }
  if (input.hasTrendError && !input.hasTrendData) {
    return false;
  }
  return (
    input.suppliersLoading ||
    input.divisionsLoading ||
    input.uploadsLoading ||
    input.trendLoading ||
    !input.hasTrendData
  );
}

export function assemblePurchaseTrendChartModel(input: {
  data: PurchaseTrendResponse | undefined;
  isLoading: boolean;
  error: unknown;
  selectedSupplierId: string | null;
  monthLabels: string[];
  fullMonthNames: string[];
}) {
  const data =
    input.data && input.data.uploadsInView.length > 0 ? input.data : undefined;
  const view = buildPurchaseTrendChartView({
    data,
    selectedSupplierId: input.selectedSupplierId,
    monthLabels: input.monthLabels,
    fullMonthNames: input.fullMonthNames,
  });
  return {
    ...view,
    cells: data?.cells ?? [],
    chartStatus: purchaseChartStatus(input.isLoading, input.error),
    unmatchedPurchaseSuppliers: data?.unmatchedPurchaseSuppliers ?? [],
    purchaseSuppliersWithoutRebateAgreement:
      data?.purchaseSuppliersWithoutRebateAgreement ?? [],
    unmatchedDivisionNames: data?.unmatchedDivisionNames ?? [],
    unmatchedPurchaseDivisions: data?.unmatchedPurchaseDivisions ?? [],
    appliedPurchaseMappings: data?.appliedPurchaseMappings ?? [],
    scopedDivisions: input.data?.scopedDivisions ?? [],
  };
}

export function buildPurchaseTrendChartView(input: {
  data: PurchaseTrendResponse | undefined;
  selectedSupplierId: string | null;
  monthLabels: string[];
  fullMonthNames: string[];
}): {
  chartSourceMonths: PurchaseTrendMonthValues[] | undefined;
  chartRows: PurchaseTrendChartRow[];
  subtitle: PurchaseTrendSubtitleModel;
  forecastUnavailableReason: PurchaseTrendForecastUnavailableReason | null;
  uploadsInView: PurchaseTrendResponse['uploadsInView'] | undefined;
  reportedAlgorithm: string | null;
  supplierDataThrough: string | null;
} {
  const selectedSeries = selectPurchaseTrendSeries(
    input.data,
    input.selectedSupplierId,
  );
  const chartSourceMonths = selectedSeries?.months ?? input.data?.months;
  const forecast = selectedSeries?.forecast ?? input.data?.forecast;
  return {
    chartSourceMonths,
    chartRows: buildPurchaseTrendChartRows(
      chartSourceMonths ?? [],
      input.monthLabels,
    ),
    subtitle: buildPurchaseTrendSubtitle({
      monthNames: input.fullMonthNames,
      lastActualMonth: forecast?.lastActualMonth ?? null,
      forecastMonths: forecastMonthsWithAmount(chartSourceMonths ?? []),
      includePriorYear: (chartSourceMonths ?? []).length > 0,
    }),
    forecastUnavailableReason: forecast?.unavailableReason ?? null,
    uploadsInView: input.data?.uploadsInView,
    reportedAlgorithm: input.data?.reportedAlgorithm ?? null,
    supplierDataThrough: supplierDataThroughLabel(
      chartSourceMonths ?? [],
      input.fullMonthNames,
    ),
  };
}

const EMPTY_COMPOSITE_MAPPING = {
  sourceColumnNames: [] as string[],
  targetColumnName: '',
  mappings: {},
  separator: '|||',
};

const GENERIC_MAPPING_TOKENS = new Set([
  'AND',
  'CANADA',
  'CENTER',
  'CENTRE',
  'CO',
  'COMPANY',
  'CORP',
  'DC',
  'DIST',
  'DISTRIBUTION',
  'INC',
  'LIMITED',
  'LLC',
  'LTD',
  'OF',
  'RWC',
  'THE',
]);

function mappingTokens(value: string): string[] {
  return value
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toUpperCase()
    .split(' ')
    .filter((token) => token.length > 1 && !GENERIC_MAPPING_TOKENS.has(token));
}

function suggestPurchaseMappingFromGroupCode(
  groupName: string,
  options: Array<{ id: string; label: string; code?: string }>,
): string {
  const groupCodes = new Set(
    Object.entries(SUPPLIER_GROUP_MAP)
      .filter(([, name]) => name === groupName)
      .map(([code]) => code),
  );
  const matches = options.filter(
    (option) => option.code !== undefined && groupCodes.has(option.code),
  );
  return matches.length === 1 ? (matches[0]?.id ?? '') : '';
}

function suggestPurchaseMappingFromGroup(
  source: string,
  options: Array<{ id: string; label: string; code?: string }>,
): string {
  const groups = new Set(
    mappingTokens(source)
      .map((token) => SUPPLIER_GROUP_MAP[token])
      .filter((name): name is string => name !== undefined),
  );
  if (groups.size !== 1) {
    return '';
  }
  const [groupName] = groups;
  if (groupName === undefined) {
    return '';
  }
  const fromCode = suggestPurchaseMappingFromGroupCode(groupName, options);
  if (fromCode) {
    return fromCode;
  }
  const groupTokens = mappingTokens(groupName);
  const exact = options.find(
    (option) => option.label.toUpperCase() === groupName.toUpperCase(),
  );
  if (exact) {
    return exact.id;
  }
  const scored = options
    .map((option) => ({
      id: option.id,
      hits: groupTokens.filter((token) =>
        mappingTokens(option.label).includes(token),
      ).length,
    }))
    .filter((row) => row.hits > 0)
    .sort((left, right) => right.hits - left.hits);
  const best = scored[0];
  if (!best || scored[1]?.hits === best.hits) {
    return '';
  }
  return best.id;
}

export function suggestPurchaseMapping(
  source: string,
  options: Array<{ id: string; label: string; code?: string }>,
): string {
  const fromGroup = suggestPurchaseMappingFromGroup(source, options);
  if (fromGroup) {
    return fromGroup;
  }
  const sourceTokens = mappingTokens(source);
  if (sourceTokens.length === 0) {
    return '';
  }
  const exact = options.find(
    (option) => option.label.toUpperCase() === source.toUpperCase(),
  );
  if (exact) {
    return exact.id;
  }
  const scored = options
    .map((option) => ({
      id: option.id,
      hits: sourceTokens.filter((token) =>
        mappingTokens(option.label).includes(token),
      ).length,
    }))
    .filter((row) => row.hits > 0)
    .sort((left, right) => right.hits - left.hits);
  const best = scored[0];
  if (!best || scored[1]?.hits === best.hits) {
    return '';
  }
  return best.id;
}

export function hasUnmatchedPurchaseRows(input: {
  unmatchedPurchaseSuppliers: unknown[];
  unmatchedDivisionNames: unknown[];
  unmatchedPurchaseDivisions: unknown[];
}): boolean {
  return (
    input.unmatchedPurchaseSuppliers.length > 0 ||
    input.unmatchedDivisionNames.length > 0 ||
    input.unmatchedPurchaseDivisions.length > 0
  );
}

function unmatchedDivisionRows(
  divisions: Array<string | { divisionName: string; branch: string | null }>,
): Array<{ divisionName: string; branch: string | null }> {
  return divisions.map((row) =>
    typeof row === 'string' ? { divisionName: row, branch: null } : row,
  );
}

export function buildUnmatchedPurchaseMappings(input: {
  suppliers: PurchaseTrendUnmatchedSupplier[];
  divisions: Array<string | { divisionName: string; branch: string | null }>;
  labels: {
    vendorNumber: string;
    vendorName: string;
    amount: string;
    division: string;
    branch?: string;
    supplier: string;
  };
  supplierOptions?: Array<{ id: string; label: string; code?: string }>;
  divisionOptions?: Array<{ id: string; label: string }>;
}): FieldMappings {
  const divisionRows = unmatchedDivisionRows(input.divisions);
  const hasBranches = divisionRows.some((row) => row.branch);
  return {
    divisionMappings: {
      sourceColumnNames: hasBranches
        ? [input.labels.division, input.labels.branch ?? 'Branch']
        : [input.labels.division],
      targetColumnName: input.labels.division,
      separator: '|||',
      mappings: Object.fromEntries(
        divisionRows.map((row) => {
          const suggestedId = suggestPurchaseMapping(
            row.branch ?? row.divisionName,
            input.divisionOptions ?? [],
          );
          const suggested =
            input.divisionOptions?.find((option) => option.id === suggestedId)
              ?.label ?? '';
          return [
            hasBranches
              ? [row.divisionName, row.branch ?? ''].join('|||')
              : row.divisionName,
            {
              type: 'simple' as const,
              value: suggested,
              matchSource: suggested ? ('direct' as const) : undefined,
            },
          ];
        }),
      ),
    },
    productMappings: { ...EMPTY_COMPOSITE_MAPPING, mappings: {} },
    supplierMappings: {
      sourceColumnNames: [
        input.labels.vendorNumber,
        input.labels.vendorName,
        input.labels.amount,
      ],
      targetColumnName: input.labels.supplier,
      separator: '|||',
      mappings: Object.fromEntries(
        input.suppliers.map((row) => {
          const suggestedId = suggestPurchaseMapping(
            `${row.supplierName} ${row.supplierCode}`,
            input.supplierOptions ?? [],
          );
          const suggested =
            input.supplierOptions?.find((option) => option.id === suggestedId)
              ?.label ?? '';
          return [
            [
              row.supplierCode,
              row.supplierName,
              formatCurrency(row.currentYearTotal, PURCHASE_CURRENCY),
            ].join('|||'),
            {
              type: 'simple' as const,
              value: suggested,
              matchSource: suggested ? ('direct' as const) : undefined,
            },
          ];
        }),
      ),
    },
  };
}

function mappingTarget(
  value: { type?: string; value?: string } | string,
): string {
  if (typeof value === 'string') {
    return value;
  }
  return value.value ?? '';
}

function resolveMappingTarget(
  value: string,
  options: Array<{ id: string; label: string }>,
): string {
  if (!value) {
    return '';
  }
  const byId = options.find((option) => option.id === value);
  if (byId) {
    return byId.id;
  }
  return options.find((option) => option.label === value)?.id ?? '';
}

export interface PurchaseAliasSave {
  entityType: 'division' | 'company';
  entityId: string;
  aliases: string[];
}

function addPurchaseAlias(
  saves: Map<string, PurchaseAliasSave>,
  entityType: PurchaseAliasSave['entityType'],
  entityId: string,
  alias: string,
) {
  if (!alias) {
    return;
  }
  const key = `${entityType}:${entityId}`;
  const save = saves.get(key) ?? { entityType, entityId, aliases: [] };
  if (!save.aliases.includes(alias)) {
    save.aliases.push(alias);
  }
  saves.set(key, save);
}

export function purchaseMappingsToSave(input: {
  mappings: FieldMappings;
  supplierOptions?: Array<{
    id: string;
    label: string;
    code?: string;
    companyId?: string;
  }>;
  divisionOptions?: Array<{ id: string; label: string }>;
}): PurchaseAliasSave[] {
  const saves = new Map<string, PurchaseAliasSave>();
  const divisionHasBranch =
    input.mappings.divisionMappings.sourceColumnNames.length > 1;
  for (const [key, value] of Object.entries(
    input.mappings.divisionMappings.mappings,
  )) {
    const target = resolveMappingTarget(
      mappingTarget(value),
      input.divisionOptions ?? [],
    );
    if (!target) {
      continue;
    }
    const [divisionName = '', branch = ''] = divisionHasBranch
      ? key.split('|||')
      : [key];
    addPurchaseAlias(
      saves,
      'division',
      target,
      branch.trim()
        ? `${divisionName.trim()}|||${branch.trim()}`
        : divisionName.trim(),
    );
  }
  for (const [key, value] of Object.entries(
    input.mappings.supplierMappings?.mappings ?? {},
  )) {
    const target = resolveMappingTarget(
      mappingTarget(value),
      input.supplierOptions ?? [],
    );
    const companyId = input.supplierOptions?.find(
      (option) => option.id === target,
    )?.companyId;
    if (!companyId) {
      continue;
    }
    const [, supplierName = ''] = key.split('|||');
    addPurchaseAlias(saves, 'company', companyId, supplierName.trim());
  }
  return [...saves.values()];
}
